import React, { useEffect, useState } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router'
import { FarmersService, FarmerCreateOptionsType } from '~/api/services/farmers'
import FarmerGroupsService from '~/api/services/farmerGroups'
import Layout from '~/components/Layout'
import notify from '~/components/Notify'
import { RouteTypes } from '~/routes/constants'
import { formatDateGMT, formatNumber, handleErrors } from '~/utils/helpers'
import DairyQuery from '~/QueryHooks/DairyQuery'
import BasicInformation from './Forms/BasicInformation'
import ManagersCertifications from './Forms/ManagersCertifications'
import Production from './Forms/Production'
import CollectionTransport from './Forms/CollectionTransport'
import Location from './Forms/Location'
import { SWISS_COUNTRY_ISO } from '../../Managers/CreateManager'

const CreateEditFarmers: React.FC = () => {
  const { t } = useTranslation()
  const { id } = useParams()
  const navigate = useNavigate()

  const [isBusy, setIsBusy] = useState(false)
  const [farmersOptions, setFarmersOptions] = useState<FarmerCreateOptionsType | null>(null)
  const [farmerGroupList, setFarmerGroupList] = useState<Array<{ value: number; text: string }>>([])
  const [isLoadingFarmerGrouplist, setIsLoadingFarmerGrouplist] = useState(false)

  const {
    data: countries,
    isLoading: isLoadingCountries,
    refetch: fetchCountries
  } = DairyQuery.getCountriesList()

  const methods = useForm({
    mode: 'onChange'
  })

  useEffect(() => {
    fetchData()
    fetchCountries()
  }, [t])

  useEffect(() => {
    const formValues = methods.getValues()
    if (!id) {
      const swissCountry = countries?.find(country => country?.iso === SWISS_COUNTRY_ISO)
      if (swissCountry) {
        if (!formValues?.mobilePrefix) {
          methods.setValue('mobilePrefix', swissCountry?.id)
        }
        if (!formValues?.countryId) {
          methods.setValue('countryId', swissCountry?.id)
        }
      }
    }
  }, [id, countries])

  async function fetchData() {
    setIsBusy(true)
    setIsLoadingFarmerGrouplist(true)

    try {
      const [responseProfileData, farmerGroupsListResponse] = await Promise.all([
        FarmersService.getFarmerOptions(),
        FarmerGroupsService.getFarmerGroupsList()
      ])

      setFarmersOptions(responseProfileData.data)
      setFarmerGroupList(farmerGroupsListResponse.data.map(farmerGroup => ({
        value: farmerGroup.id,
        text: `${farmerGroup.name} (${formatNumber(farmerGroup.amountPercent)}%)`
      })))

      if (id) {
        const response = await FarmersService.getFarmerDetails(id)
        if (!response.data.farmer.districtId && response.data.farmer.zip) {
          const zipData = await FarmersService.getZipDistrictState({ zip: response.data.farmer.zip })
          response.data.farmer.districtId = zipData.data.id
        }

        const fileResponse = await FarmersService.getFarmerFile(id)

        methods.reset({
          ...response.data.farmer,
          farmerType: response.data.farmer.parentId ? 'sub' : 'main'
        })

        if (fileResponse.data) {
          methods.setValue('logo', fileResponse.data)
        }
      }
    } catch (error) {
      handleErrors(error)
    }

    setIsBusy(false)
    setIsLoadingFarmerGrouplist(false)
  }

  async function onSubmit(data) {
    if (!id && data?.farmerType === 'sub') {
      delete data.farmerGroupId
    }

    data.joiningDate = formatDateGMT(data.joiningDate)
    data.longitude = data?.longitude?.toString()?.replace(',', '.')
    data.latitude = data?.latitude?.toString()?.replace(',', '.')

    setIsBusy(true)
    try {
      if (!id) {
        await FarmersService.createFarmer(data)
        notify(t('Farmer created'), 'success')
      } else {
        await FarmersService.updateFarmerDetails(id, data)
        notify(t('Farmer updated'), 'success')
      }
      navigate(RouteTypes.DAIRY_FACTORY_FARMERS)
    } catch (error) {
      handleErrors(error)
    }
    setIsBusy(false)
  }

  function renderForm() {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <BasicInformation
          farmerGroupList={farmerGroupList}
          isLoadingFarmerGrouplist={isLoadingFarmerGrouplist}
          countries={countries || []}
        />
        <ManagersCertifications
          farmerOptions={farmersOptions}
          isLoadingOptions={isBusy}
        />
        <Production farmerOptions={farmersOptions} />
        <CollectionTransport farmerOptions={farmersOptions} />
        <Location
          farmerOptions={farmersOptions}
          countries={countries || []}
        />
      </div>
    )
  }

  return (
    <Layout
      isBusy={isBusy || isLoadingCountries}
      segmented
      close={RouteTypes.DAIRY_FACTORY_FARMERS}
      title={id ? t('Edit farmer') : t('Create farmer')}
      action={id ? t('Update') : t('Save')}
      onSubmit={methods.handleSubmit(onSubmit)}
    >
      <FormProvider {...methods}>
        {farmersOptions && !isBusy && renderForm()}
      </FormProvider>
    </Layout>
  )
}

export default CreateEditFarmers
